import {t} from 'i18next'
import {Button} from 'primereact/button'
import {Column} from 'primereact/column'
import {ConfirmDialog} from 'primereact/confirmdialog'
import {DataTable} from 'primereact/datatable'
import {Dropdown, DropdownChangeParams} from 'primereact/dropdown'
import {InputText} from 'primereact/inputtext'
import {Toast} from 'primereact/toast'
import {Tooltip} from 'primereact/tooltip'
import React, {useRef, useState} from 'react'
import {useFormContext} from 'react-hook-form'
import {PhoneTypeCt} from '../../../model/enums/enums'
import {updatePersonInStore} from '../../../redux/basic/updatePerson'
import {useAppDispatch} from '../../../redux/hooks'
import {updatePersonInDb} from '../../../services/api/mokaiApiService'
import {Phone, PhoneType, phoneTypes} from '../../../util/phone'
import {actionBodyDelete} from '../data-table/cell-body/ActionBody'
import {FormattedPhoneNumberTableEntry} from '../formatted-phone-number/FormattedPhoneNumber'

// new phone component
export const PhoneComponent = (): JSX.Element => {
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
  const [phoneToDelete, setPhoneToDelete] = useState<Phone>()
  const toast = useRef<Toast>(null)

  // to deal with the way the whole form is implemented
  // we need to keep a separate list of phone numbers in case the user is not yet created
  const [currentType, setCurrentType] = useState<PhoneTypeCt>(
    PhoneTypeCt.mobile
  )

  const [currentNumber, setCurrentNumber] = useState<string>('')

  const dispatcher = useAppDispatch()

  const { getValues, setValue, reset } = useFormContext()

  const typeCellBody = (p: Phone) => {
    const pT = phoneTypes.find(
      (type: PhoneType) => type.value === p.phone_type
    )
    if (!pT) {
      return <></>
    }
    return (
      <div className={`target-${pT.label} ml-auto mr-auto`}>
        <span className={`${pT.icon} icon-dark-gray `} />
        <Tooltip
          target={`.target-${pT?.label}`}
          content={pT.label}
          showDelay={500}
        />
      </div>
    )
  }

  const setPhoneForDeleteDialog = (phoneToRemove: Phone): void => {
    setPhoneToDelete(phoneToRemove)
    setOpenDeleteDialog(true)
  }

  const closeDialog = () => {
    setPhoneToDelete(undefined)
    setOpenDeleteDialog(false)
  }

  const deletePhoneNumber = async () => {
    // this should never happen
    if (!phoneToDelete) {
      console.warn('No phone number chosen. Delete fails')
      return
    }

    const phoneId = phoneToDelete.phone_id
    const body = {
      phone: [
        {
          phone_id: phoneId,
          delete: true,
        },
      ],
    }
    const personId = getValues('person_id')
    // if we added a number to a person that is not yet saved we can still delete it
    if (!personId || phoneToDelete.phone_id <= 0) {
      // phone number must exist so cannot be undefined
      const new_phones = getValues('phone').filter(
        (e: Phone) => e.phone_id !== phoneToDelete.phone_id
      )
      setValue('phone', new_phones)
      setOpenDeleteDialog(false)
      setPhoneToDelete(undefined)

      return
    }

    const res = await updatePersonInDb(personId, body)
    const newData = res.data
    if (!newData) {
      toast?.current?.show({
        className: '',
        closable: false,
        content: undefined,
        contentClassName: '',
        contentStyle: undefined,
        sticky: false,
        style: undefined,
        severity: 'error',
        summary: t<string>('core.action.phone.delete.failed.title'),
        detail: t<string>('core.action.phone.delete.failed.message'),
        life: 5000,
      })

      setOpenDeleteDialog(false)
      setPhoneToDelete(undefined)
      return
    }

    if (!newData.phone) {
      newData.phone = undefined
    }
    // we need to take care of type since we deal with different kinds of person here
    const currentPerson = getValues()
    const updatedPerson = {
      ...currentPerson,
      ...newData,
    }
    // here, we can only set the phone number since we can be sure that was the only thing that changed
    reset(updatedPerson)
    updatePersonInStore(updatedPerson, dispatcher)
    setOpenDeleteDialog(false)
    setPhoneToDelete(undefined)
  }

  const typeTemplate = (option: any) => {
    if (!option?.label) {
      return (
        <div style={{ minWidth: '120px' }}>
          <i className={'fas fa-question-circle icon-dark-gray mr-2'} />{' '}
          <span>{t<string>('core.select')}</span>
        </div>
      )
    }
    return (
      <div style={{ minWidth: '120px' }}>
        <i className={option?.icon + ' icon-dark-gray mr-2'} />{' '}
        <span>{option?.label}</span>
      </div>
    )
  }

  const onAddNumberClick = () => {
    const phones: Phone[] = getValues('phone') || []

    if (!currentNumber) {
      return
    }

    // we add the new numbers with a dummy value
    // we make the unsaved ones recognizable by making it negative
    phones.push({
      phone_id: -phones.length,
      phone_type: currentType,
      phone_number: currentNumber,
      last_change: '0',
      is_active: 1,
    })

    setValue('phone', phones)
    setCurrentNumber('')
    setCurrentType(PhoneTypeCt.mobile)
  }

  return (
    <>
      <DataTable
        header={t<string>('person.phone_list')}
        className={'p-datatable field'}
        value={getValues('phone')}
        dataKey="phone_id"
        scrollable
        emptyMessage={t<string>('phone.no_number')}
        scrollHeight={'20vh'}
        rowHover
        stripedRows
        columnResizeMode="expand"
        responsiveLayout={'scroll'}
      >
        <Column
          field="phone_type"
          body={typeCellBody}
          header={t<string>('core.type')}
          style={{ minWidth: '3em', maxWidth: '3em' }}
        />
        <Column
          field="phone_number"
          body={(row) => FormattedPhoneNumberTableEntry(row.phone_number)}
          header={t<string>('phone.number')}
        />
        <Column
          body={actionBodyDelete(setPhoneForDeleteDialog)}
          style={{ minWidth: '2em', maxWidth: '2em', padding: '0' }}
        />
      </DataTable>

      <div className={'col-12 m-0 p-0 p-fluid grid align-items-center'}>
        <div className="field col-12 md:col-3">
          <Dropdown
            appendTo={document.body}
            id={'phoneDropdown'}
            options={phoneTypes}
            value={currentType}
            optionLabel={'label'}
            onChange={(e: DropdownChangeParams) => {
              setCurrentType(e.target.value)
            }}
            placeholder={t<string>('core.select')}
            emptyMessage={t<string>('core.empty_message')}
            itemTemplate={typeTemplate}
            valueTemplate={typeTemplate}
            className={'p-dropdown'}
          />
        </div>

        <div className="field col-12 md:col ">
          <InputText
            className={''}
            value={currentNumber}
            placeholder={'+41 79 123 45 67'}
            maxLength={60}
            id={'phone_number'}
            onChange={(e: any) => {
              setCurrentNumber(e.target.value)
            }}
          />
        </div>
        <div className={'field col-12 md:col-3'}>
          <Button
            type={'button'}
            label={t<string>('core.add')}
            className="p-button-raised p-button-text ml-auto"
            onClick={onAddNumberClick}
          />
        </div>
      </div>

      <ConfirmDialog
        visible={openDeleteDialog}
        header={t<string>('core.action.phone.delete.delete_dialog.title')}
        // TODO
        message={t<string>('core.action.phone.delete.delete_dialog.question', {
          number: phoneToDelete?.phone_number,
        })}
        onHide={closeDialog}
        reject={closeDialog}
        accept={deletePhoneNumber}
      />
    </>
  )
}